import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuth from "CustomHooks/useAuth";
import Button from "Components/Atoms/Button";
import {
    analyticsConfigured,
    analyticsConsent,
    ANALYTICS_CONSENT_EVENT,
    setAnalyticsConsent,
    setAnalyticsPage,
    startAnalytics,
    resetAnalyticsIdentity
} from "utils/analytics";
import "./styles.scss";

export default function ProductAnalytics() {
    const { uid, restaurantId } = useAuth();
    const location = useLocation();
    const { t } = useTranslation();
    const [choice, setChoice] = useState(analyticsConsent);
    const [open, setOpen] = useState(false);
    const [storageError, setStorageError] = useState(false);
    const previousUser = useRef(uid);
    useEffect(() => {
        if (previousUser.current !== uid) resetAnalyticsIdentity();
        previousUser.current = uid;
    }, [uid]);
    useEffect(() => {
        setAnalyticsPage(
            location.pathname,
            uid ? restaurantId || undefined : undefined
        );
    }, [location.pathname, restaurantId, uid]);
    useEffect(() => startAnalytics(), []);
    useEffect(() => {
        const sync = () => setChoice(analyticsConsent());
        window.addEventListener("storage", sync);
        window.addEventListener(ANALYTICS_CONSENT_EVENT, sync);
        return () => {
            window.removeEventListener("storage", sync);
            window.removeEventListener(ANALYTICS_CONSENT_EVENT, sync);
        };
    }, []);
    const choose = (granted: boolean) => {
        if (!setAnalyticsConsent(granted)) {
            setStorageError(true);
            return;
        }
        setChoice(granted ? "granted" : "denied");
        setOpen(false);
        setStorageError(false);
    };
    if (!analyticsConfigured || !uid) return null;
    return (
        <aside
            className="product-analytics"
            aria-label={t("common_Analytics settings")}
        >
            {choice !== null && !open ? (
                <Button variant="transparent" onClick={() => setOpen(true)}>
                    {t("common_Analytics settings")}
                </Button>
            ) : (
                <section aria-labelledby="product-analytics-title">
                    <h2 id="product-analytics-title">
                        {t("common_Optional product analytics")}
                    </h2>
                    <p>{t("common_Analytics explanation")}</p>
                    <p>{t("common_Analytics storage explanation")}</p>
                    <a
                        href="https://gastronaut.ai/datenschutz#tracking"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {t("common_Privacy")}
                    </a>
                    <div className="product-analytics-actions">
                        <Button
                            variant="outlined"
                            onClick={() => choose(false)}
                        >
                            {t("common_Decline analytics")}
                        </Button>
                        <Button variant="outlined" onClick={() => choose(true)}>
                            {t("common_Allow analytics")}
                        </Button>
                    </div>
                    {storageError && (
                        <p role="alert">
                            {t("common_Analytics storage error")}
                        </p>
                    )}
                </section>
            )}
        </aside>
    );
}
